import { TextStyle } from '@tiptap/extension-text-style';
import { Typography } from '@tiptap/extension-typography';
import { Placeholder, Selection } from '@tiptap/extensions';
import { Markdown } from '@tiptap/markdown';
import type { Content, Editor, UseEditorOptions } from '@tiptap/react';
import { useEditor, useEditorState } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import * as React from 'react';
import { toast } from 'sonner';

import { cn } from 'utils/cn';

import {
    CodeBlockLowlight,
    Color,
    FileHandler,
    HorizontalRule,
    MentionSuggestionItem,
    MinimalTiptapMention,
    ResetMarksOnEnter,
    UnsetAllMarks,
} from '../extensions';
import { fileToBase64, getOutput } from '../utils';
import { useThrottle } from './use-throttle';

export interface UseMinimalTiptapEditorProps extends UseEditorOptions {
    value?: Content;
    output?: 'html' | 'json' | 'text' | 'markdown';
    placeholder?: string;
    editorClassName?: string;
    throttleDelay?: number;
    mentions?: MentionSuggestionItem[];
    onUpdate?: (content: Content) => void;
    onBlur?: (content: Content) => void;
}

const createExtensions = ({
    mentionsGetter,
    placeholder,
    markdown,
}: {
    mentionsGetter?: () => MentionSuggestionItem[];
    placeholder: string;
    markdown: boolean;
}) => [
    StarterKit.configure({
        blockquote: { HTMLAttributes: { class: 'block-node' } },
        // bold
        bulletList: { HTMLAttributes: { class: 'list-node' } },
        code: { HTMLAttributes: { class: 'inline', spellcheck: 'false' } },
        codeBlock: false,
        // document
        dropcursor: { width: 2, class: 'ProseMirror-dropcursor border' },
        // gapcursor
        // hardBreak
        heading: { HTMLAttributes: { class: 'heading-node' } },
        // undoRedo
        horizontalRule: false,
        // italic
        // listItem
        // listKeymap
        link: {
            enableClickSelection: true,
            openOnClick: false,
            HTMLAttributes: {
                class: 'link',
            },
        },
        orderedList: { HTMLAttributes: { class: 'list-node' } },
        paragraph: { HTMLAttributes: { class: 'text-node' } },
        // strike
        // text
        // underline
        // trailingNode
    }),
    // Markdown mode: only extensions whose content survives markdown serialization.
    // FileHandler (base64 images), Color/TextStyle and mentions would be silently
    // dropped by getMarkdown(), so they are rich-mode only.
    ...(markdown
        ? [Markdown]
        : [
              FileHandler.configure({
                  allowBase64: true,
                  allowedMimeTypes: ['image/*'],
                  maxFileSize: 5 * 1024 * 1024,
                  onDrop: (editor, files, pos) => {
                      files.forEach(async (file) => {
                          const src = await fileToBase64(file);
                          editor.commands.insertContentAt(pos, {
                              type: 'image',
                              attrs: { src },
                          });
                      });
                  },
                  onPaste: (editor, files) => {
                      files.forEach(async (file) => {
                          const src = await fileToBase64(file);
                          editor.commands.insertContent({
                              type: 'image',
                              attrs: { src },
                          });
                      });
                  },
                  onValidationError: (errors) => {
                      errors.forEach((error) => {
                          toast.error('Image validation error', {
                              position: 'bottom-right',
                              description: error.reason,
                          });
                      });
                  },
              }),
              Color,
              TextStyle,
              ...(mentionsGetter ? [MinimalTiptapMention(mentionsGetter)] : []),
          ]),
    Selection,
    Typography,
    UnsetAllMarks,
    HorizontalRule,
    ResetMarksOnEnter,
    CodeBlockLowlight,
    Placeholder.configure({ placeholder: () => placeholder }),
];

export const useMinimalTiptapEditor = ({
    value,
    output = 'html',
    placeholder = '',
    editorClassName,
    throttleDelay = 0,
    mentions,
    onUpdate,
    onBlur,
    ...props
}: UseMinimalTiptapEditorProps) => {
    const mentionsRef = React.useRef<MentionSuggestionItem[] | undefined>(mentions);
    mentionsRef.current = mentions;
    const mentionsGetter = mentions ? () => mentionsRef.current ?? [] : undefined;

    const throttledSetValue = useThrottle((content: Content) => onUpdate?.(content), throttleDelay);

    const handleUpdate = React.useCallback(
        (editor: Editor) => throttledSetValue(getOutput(editor, output)),
        [output, throttledSetValue],
    );

    const handleCreate = React.useCallback(
        (editor: Editor) => {
            if (value && editor.isEmpty) {
                editor.commands.setContent(value, output === 'markdown' ? { contentType: 'markdown' } : undefined);
            }
        },
        [value, output],
    );

    const handleBlur = React.useCallback((editor: Editor) => onBlur?.(getOutput(editor, output)), [output, onBlur]);

    const editor = useEditor({
        immediatelyRender: false,
        extensions: createExtensions({ mentionsGetter, placeholder, markdown: output === 'markdown' }),
        editorProps: {
            attributes: {
                autocomplete: 'off',
                autocorrect: 'off',
                autocapitalize: 'off',
                class: cn('focus:outline-hidden', editorClassName),
            },
        },
        onUpdate: ({ editor: editorInstance }) => handleUpdate(editorInstance),
        onCreate: ({ editor: editorInstance }) => handleCreate(editorInstance),
        onBlur: ({ editor: editorInstance }) => handleBlur(editorInstance),
        ...props,
    });

    // Transaction subscription only: re-renders consumers (toolbar active states)
    // whenever the document or selection changes. Since @tiptap/react 3.27 this must
    // not be the source of the editor instance — the state manager's snapshot keeps
    // the initial null editor until the first transaction, so an editor with empty
    // initial content would never surface. useEditor already re-renders on instance
    // creation, so return its editor directly. The equalityFn must also stay
    // identity-based: the 3.27 default is a deep equal that treats fresh closures as
    // never-equal, which causes an infinite render loop.
    useEditorState({
        editor,
        selector: (context) => context.editor?.state,
        equalityFn: (a, b) => a === b,
    });

    return editor;
};

export default useMinimalTiptapEditor;
